/**
 * UNE NUIT « REPRISE DE L'HISTORIQUE » — miroir de `RepriseHistorique` du
 * cœur (`packages/core/src/finance/reprise-provenance.ts`).
 *
 * La reprise (phase 1, décision du 15/10/2026) valide chaque nuit passée au
 * « Total » du suivi de la DAF. Ces nuits sont des nuits VALIDÉES comme les
 * autres (même état, même place dans le chiffre d'affaires), mais personne ne
 * les a validées à l'écran : dire « Validée par Hugo le 16 octobre 2026 » d'une
 * nuit de 2017 serait faux. Partout où une nuit validée se montre, celle-ci
 * dit d'où vient son chiffre — avec la phrase que le SERVEUR fabrique
 * (`label`), pour que tous les écrans disent la même chose.
 *
 * Un serveur d'avant la reprise n'envoie pas `history` : lu comme `null`, la
 * nuit reste « Validée sans clôture ».
 */
export interface RepriseHistorique {
  /** « Repris de l’historique (Planning Direction, saison 2025-26) ». */
  label: string
  season: string
  sheet: string
  row: number
  /**
   * La ligne du classeur porte une autre date que la nuit (onglet décalé d'un
   * jour) : pourquoi, en clair. `null` = la ligne dit la bonne date. Sans
   * elle, qui relit la ligne y trouve le lendemain et croit à une erreur.
   */
  date_correction: string | null
}

export function repriseDe(v: unknown): RepriseHistorique | null {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return null
  const o = v as Record<string, unknown>
  if (typeof o['label'] !== 'string' || o['label'] === '' || typeof o['sheet'] !== 'string' || typeof o['row'] !== 'number') return null
  const dc = typeof o['date_correction'] === 'string' && o['date_correction'] !== '' ? o['date_correction'] : null
  return { label: o['label'], season: typeof o['season'] === 'string' ? o['season'] : '', sheet: o['sheet'], row: o['row'], date_correction: dc }
}

/** Le libellé court d'une pastille ou d'une case : la saison est dans l'infobulle. */
export const REPRIS_COURT = 'Repris de l’historique'

/** « « Suivi CA 2026 », ligne 51 » — où relire le chiffre dans le classeur de la DAF. */
export function ouDansLeClasseur(r: RepriseHistorique): string {
  return `« ${r.sheet} », ligne ${r.row}`
}

/**
 * La phrase entière d'une nuit reprise : d'où, où la relire, et — si la ligne
 * porte une autre date — que la date a été corrigée exprès. La même partout
 * (fiche, annulation, chiffre d'affaires).
 */
export function phraseReprise(r: RepriseHistorique): string {
  return `${r.label}, ${ouDansLeClasseur(r)}${r.date_correction ? ` (date corrigée : ${r.date_correction})` : ''}`
}
